import { type JSX, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { X } from "lucide-solid";
import { cn } from "@bso/shared";
import { useBackDismiss } from "@/lib/use-back-dismiss";

interface ResponsiveSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /**
   * Content factory. Called once per surface (mobile sheet and desktop
   * dropdown) so each gets its own DOM instance — passing a plain JSX
   * element would move the same nodes between containers.
   */
  children: () => JSX.Element;
  /** Extra classes for the desktop dropdown panel (width, alignment…). */
  dropdownClass?: string;
}

/**
 * Filter container with the legacy mobile interaction pattern: a bottom
 * sheet under `md`, an anchored dropdown on `md` and up. The trigger button
 * stays in the caller; its parent must be `relative` for the dropdown.
 */
export function ResponsiveSheet(props: ResponsiveSheetProps) {
  // Browser "back" closes the sheet instead of navigating (premium PWA pattern).
  useBackDismiss(
    () => props.open,
    () => {
      props.onClose();
    },
  );

  return (
    <Show when={props.open}>
      {/* Mobile bottom sheet */}
      <Portal>
        <div class="md:hidden fixed inset-0 z-[90]">
          <button
            type="button"
            aria-label="Cerrar"
            class="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-xs border-none p-0 cursor-default"
            onClick={() => {
              props.onClose();
            }}
          />
          <div class="absolute bottom-0 left-0 right-0 max-h-[70dvh] bg-card border-t border-border/50 rounded-t-[28px] shadow-2xl flex flex-col pb-[env(safe-area-inset-bottom,0px)] animate-in slide-in-from-bottom duration-200">
            <div class="w-12 h-1.5 rounded-full bg-muted-foreground/30 mx-auto mt-3 shrink-0" />
            <div class="flex items-center justify-between px-5 pt-3 pb-2 shrink-0">
              <span class="text-sm font-bold text-foreground">{props.title}</span>
              <button
                type="button"
                onClick={() => {
                  props.onClose();
                }}
                class="p-2 -mr-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Cerrar"
              >
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="flex-1 overflow-y-auto px-3 pb-4">{props.children()}</div>
          </div>
        </div>
      </Portal>

      {/* Desktop anchored dropdown */}
      <div
        class={cn(
          "hidden md:block absolute right-0 mt-2 w-64 max-h-80 overflow-y-auto bg-card border border-border/60 rounded-xl shadow-lg z-50 p-2",
          props.dropdownClass,
        )}
      >
        {props.children()}
      </div>
    </Show>
  );
}
